import { ChangeDetectionStrategy, Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { BillingService, BillingStepperDialogService } from '@examdojo/billing';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { mapToVoid } from '@examdojo/core/rxjs';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { QuestionAttemptEntityQuery, QuestionAttemptEntityService, QuestionAttemptHttpModel } from '@examdojo/question';
import { RiveDirective } from '@examdojo/ui/rive';
import { IonContent } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { debounceTime, EMPTY, filter, map, Observable, switchMap, tap } from 'rxjs';
import { FullscreenFlowStepComponent } from '../../../../shared/fullscreen-flow';
import { FullscreenFlowDialogService } from '../../../../shared/fullscreen-flow/fullscreen-flow-dialog.service';
import { LoadingSenseiComponent } from './loading-sensei/loading-sensei.component';

@Component({
  selector: 'dojo-grading-loading',
  imports: [ButtonComponent, RiveDirective, TranslocoPipe, IonContent, LoadingSenseiComponent],
  templateUrl: './grading-loading.component.html',
  styleUrl: './grading-loading.component.scss',
  host: { class: 'ion-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GradingLoadingComponent extends FullscreenFlowStepComponent {
  constructor(
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly fullscreenFlowDialogService: FullscreenFlowDialogService,
    private readonly billingService: BillingService,
    private readonly destroyRef: DestroyRef,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly billingStepperDialogService: BillingStepperDialogService,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
  ) {
    super();

    this.onStatus('GRADED')
      .pipe(
        // A small timeout to help render the Rive elements on the second screen.
        // Not sure what's causing the issues, they seem to be quite random,
        // maybe race conditions, but not sure what's causing them.
        debounceTime(50),
        switchMap(() => this.goToNextStep()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly state = connectState({
    gradingFailed: this.questionAttemptQuery.attemptStatus$.pipe(map((status) => status === 'FAILED')),
    gradingError: this.questionAttemptQuery.gradingError$,
    finishedGrading: this.questionAttemptQuery.attemptStatus$.pipe(map((status) => status !== 'SUBMITTED')),
    useBillingStepper: this.featureFlagService.select(ExamDojoFeatureFlag.Billing),
  });

  dismissDialog(shouldRecapture: boolean) {
    if (shouldRecapture) {
      this.questionAttemptService.setOpenCaptureDialogFlag(true);
    }
    this.fullscreenFlowDialogService.dismiss();
  }

  openBillingDialog() {
    this.fullscreenFlowDialogService.dismiss();

    this.billingStepperDialogService.openDialog();
  }

  openProPlanCheckout() {
    this.billingService
      .fetchHostedCheckoutSessionUrl()
      .pipe(
        tap((checkoutUrl) => {
          window.location.href = checkoutUrl;
        }),
        this.errorHandlerService.catchError('[BillingComponent]: Failed to generate checkout session', () => EMPTY, {
          toast: 'Failed to open checkout session',
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private onStatus(status: QuestionAttemptHttpModel['status']): Observable<void> {
    return this.questionAttemptQuery.attemptStatus$.pipe(
      filter((attemptStatus) => attemptStatus === status),
      mapToVoid(),
    );
  }
}
